import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import FontAwesome from 'utils/FA';
import Routes from '../../../routes';
import ContactList from '../ContactList/ContactList';
import { ContactRow } from '../ContactList/ContactRows';
import { currencyColumn, expanderColumn, linkColumn } from '../ContactList/ColumnDefinitions';

const nameValue = ({ firstName, id, lastName }) => {
  if (id && firstName && lastName) {
    return `${lastName}, ${firstName}`;
  }
  return lastName;
};

const linkValue = ({ firstName, id, lastName }) => {
  if (id && firstName && lastName) {
    return Routes.contact_path(id);
  }
  return '';
};

const accountProperties = (contact) => ({
  firstName: contact.account_first_name,
  id: contact.account_contact_id,
  lastName: contact.account_last_name
});

const studentProperties = (contact) => ({
  firstName: contact.student_first_name,
  id: contact.student_contact_id,
  lastName: contact.student_last_name || 'Account Charge'
});


const fillOutColumns = (columns) => [
  expanderColumn({}),
  linkColumn({
    ...columns[0],
    value: (contact) => nameValue(accountProperties(contact)),
    link: (contact) => linkValue(accountProperties(contact))
  }),
  linkColumn({
    ...columns[1],
    value: (contact) => '',
    subRowValue: (contact) => nameValue(studentProperties(contact)),
    link: (contact) => linkValue(studentProperties(contact))
  }),
  currencyColumn({
    value: 'charged_total',
    defaultToZero: true,
    ...columns[2]
  }),
  currencyColumn({
    ...columns[3],
    value: 'credited_total',
    defaultToZero: true
  }),
  currencyColumn({
    ...columns[4],
    value: 'paid_in_school_total',
    defaultToZero: true
  }),
  currencyColumn({
    ...columns[5],
    value: 'paid_online_total',
    defaultToZero: true
  }),
  currencyColumn({
    ...columns[6],
    value: 'convenience_fee_total',
    subRowValue: (contact) => <td className='contact-list-react-type-currency'>-</td>,
    defaultToZero: true
  }),
  currencyColumn({
    ...columns[7],
    value: 'balance',
    defaultToZero: true
  }),
  currencyColumn({
    ...columns[8],
    value: 'pending_total',
    subRowValue: (contact) => <td className='contact-list-react-type-currency'>-</td>,
    defaultToZero: true
  }),
  currencyColumn({
    ...columns[9],
    value: 'platform_fee_total',
    subRowValue: (contact) => <td className='contact-list-react-type-currency'>-</td>,
    defaultToZero: true
  }),
  currencyColumn({
    ...columns[10],
    value: 'merchant_fee_total',
    subRowValue: (contact) => <td className='contact-list-react-type-currency'>-</td>,
    defaultToZero: true
  }),
  currencyColumn({
    ...columns[11],
    value: 'remitted_total',
    subRowValue: (contact) => <td className='contact-list-react-type-currency'>-</td>,
    defaultToZero: true
  })
];

const BillingARReportList = ({
  billingAccounts,
  columns,
  schoolYearId,
  emptyMessage,
  onSort,
  sort,
  showModal,
  searchParams
}) => (
  <ContactList
    onSort={onSort}
    sort={sort}
    columns={fillOutColumns(columns)}
    contacts={billingAccounts.map(account => ({ ...account, id: account.account_id }))}
    emptyMessage={emptyMessage}
    makeSubRows={(props) => (
      <BillingARReportListSubEntry
        {...props}
        showModal={showModal}
        searchParams={searchParams}
        schoolYearId={schoolYearId}
      />
    )}
  />
);

const BillingARReportListSubEntry = (props) => {
  const {
    schoolYearId,
    searchParams,
    showModal,
    contact,
    columns
  } = props;
  const [ready, setReady] = useState(false);
  const [res, setRes] = useState({ rows: [] });

  const billingAccountId = contact.account_id;

  useEffect(() => {
    fetch(`/api/billing_ar_report/${billingAccountId}?${searchParams}`)
      .then((response) => response.json())
      .then((json) => {
        setRes(json);
        setReady(true);
      })
      .catch(() => showModal('UnexpectedError'));
  }, [billingAccountId, schoolYearId]);

  if (!ready) {
    return (
      <tr>
        <td />
        <td colSpan={12}>
          <FontAwesome name='spinner' spin postNbsp />
          Loading...
        </td>
      </tr>
    );
  }

  return res.rows.map(subRowObj => (
    <ContactRow
      className='contact-list-react-sub-row'
      contact={subRowObj}
      columns={columns}
      key={`${subRowObj.account_contact_id}_${subRowObj.student_contact_id}`}
    />
  ));
};

const Column = PropTypes.shape({
  header: PropTypes.string,
  attribute: PropTypes.string,
  type: PropTypes.string,
  sortName: PropTypes.string,
  hideFromTotal: PropTypes.bool
});

const BillingAccount = PropTypes.shape({
  account_contact_id: PropTypes.string,
  account_first_name: PropTypes.string,
  account_last_name: PropTypes.string,
  balance: PropTypes.string,
  charged_total: PropTypes.string,
  credited_total: PropTypes.string,
  paid_online_total: PropTypes.string,
  convenience_fee_total: PropTypes.string,
  platform_fee_total: PropTypes.string,
  merchant_fee_total: PropTypes.string,
  paid_in_school_total: PropTypes.string,
  pending_total: PropTypes.string,
  remitted_total: PropTypes.string
});

BillingARReportList.propTypes = {
  /** Columns in the table */
  columns: PropTypes.arrayOf(Column),

  schoolYearId: PropTypes.string.isRequired,

  /** Billing accounts in the report */
  billingAccounts: PropTypes.arrayOf(BillingAccount),

  /** Message to be shown when there are no results */
  emptyMessage: PropTypes.string,

  /** The selected sort */
  onSort: PropTypes.func,

  /** Selected sort */
  sort: PropTypes.shape({
    column: PropTypes.string,
    order: PropTypes.string
  }),

  /** Function to show a modal during if an error occurs */
  showModal: PropTypes.func,

  /** The current set of query params: school_year_id, date_range, sort */
  searchParams: PropTypes.instanceOf(URLSearchParams)
};

const columnList = require('../../../../config/ar_report_columns.json');

BillingARReportList.defaultProps = {
  columns: columnList,
  billingAccounts: [],
  emptyMessage: '',
  sort: {},
  searchParams: new URLSearchParams(),
  onSort: () => false,
  showModal: () => {}
};

BillingARReportListSubEntry.propTypes = {
  schoolYearId: PropTypes.string.isRequired,
  showModal: PropTypes.func.isRequired,
  searchParams: PropTypes.instanceOf(URLSearchParams).isRequired,
  contact: BillingAccount.isRequired,
  columns: PropTypes.arrayOf(Column).isRequired
};

export default BillingARReportList;
